import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createDistributor as createDistributorFn,
  updateDistributor as editDistributorService,
  deleteDistributor as deleteDistributorService,
} from "../services/distributor-service";
import { ApiError } from "pages/admin-app/shared/models/response/api-errors";
import { AxiosError } from "axios";
import { CreateDistributorResponse } from "../models/response/create-distributor-response";
import { CreateDistributorRequest } from "../models/request/create-distributor-request";
import { EditDistributorRequest } from "../models/request/edit-distributor-request";
import { EditDistributorResponse } from "../models/response/edit-distributor-response";

interface Props {
  id?: number | string;
  onEditSuccess?: () => void;
  onEditError?: () => void;
  onCreateSuccess?: () => void;
  onCreateError?: () => void;
}

export interface ApiErrorResponse {
  message: string[];
  error: string;
  statusCode: number;
}

export default function useMutateDistributor(props?: Props) {
  const queryClient = useQueryClient();

  const {
    mutate: editDistributor,
    error: editDistributorError,
    isPending: editDistributorLoading,
    isSuccess: editDistributorSuccess,
  } = useMutation<
    EditDistributorResponse,
    AxiosError<ApiErrorResponse>,
    EditDistributorRequest
  >({
    mutationKey: ["distributor", props?.id, "edit"],
    mutationFn: editDistributorService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["distributor", props?.id] });
      queryClient.invalidateQueries({ queryKey: ["distributors"] });
      props?.onEditSuccess && props?.onEditSuccess();
    },
    onError: () => {
      props?.onEditError && props?.onEditError();
    },
  });

  const {
    mutate: deleteDistributor,
    error: deleteDistributorError,
    isPending: deleteDistributorLoading,
    reset: deleteDistributorReset,
    isSuccess: deleteDistributorSuccess,
    isError: deleteDistributorIsError,
  } = useMutation({
    mutationKey: ["distributor", props?.id, "delete"],
    mutationFn: deleteDistributorService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["distributors"] });
    },
  });

  const {
    mutate: createDistributor,
    error: createDistributorError,
    isPending: createDistributorLoading,
    isSuccess: createDistributorSuccess,
  } = useMutation<
    CreateDistributorResponse,
    ApiError,
    CreateDistributorRequest
  >({
    mutationKey: ["distributor", props?.id, "create"],
    mutationFn: createDistributorFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["distributors"] });
      props?.onCreateSuccess && props?.onCreateSuccess();
    },
    onError: () => {
      props?.onCreateError && props?.onCreateError();
    },
  });

  return {
    editDistributor,
    editDistributorError,
    editDistributorLoading,
    editDistributorSuccess,

    deleteDistributor,
    deleteDistributorError,
    deleteDistributorLoading,
    deleteDistributorReset,
    deleteDistributorSuccess,
    deleteDistributorIsError,

    createDistributor,
    createDistributorError,
    createDistributorLoading,
    createDistributorSuccess,
  };
}
